import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { useCart } from '../contexts/CartContext';
import { keyframes } from '@emotion/react';
import InfoIcon from '@mui/icons-material/Info';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import settings from '../config/settings';
import { getUsersFromCache } from '../utils/userCache';
import { get, post, put } from '../utils/api';
import { createWooCommerceOrder, completeCcOrder, failCcOrder, cancelCcOrder } from '../utils/orderUtils';
import {
  buildGuestOrderCustomer,
  validateGuestReceiptInput,
  getGuestStripeReceiptFields
} from '../utils/guestCheckout';
import { useKeyboard } from '../context/KeyboardContext';
import {
  getLineChargeTotal,
  getLineListTotal,
  getPartialMinPay,
  isPartialPaymentEligible,
  isPartialLineAmountUnset,
  PARTIAL_MIN_LINE_DOLLARS,
  PARTIAL_MIN_PAY_FRACTION
} from '../utils/cartPricing';
import { 
  createTerminalPaymentIntent,
  processPaymentIntentOnReader,
  getPaymentIntentStatus,
  capturePaymentIntent,
  cancelPaymentIntent,
  updatePaymentIntentMetadata,
  clearTerminal,
} from '../utils/paymentUtils';
import { useSessionTimeout } from '../context/SessionTimeoutContext';
// Removed Stripe Elements manual entry; only Terminal (tap/insert/swipe) is supported
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Divider, 
  Button, 
  CircularProgress, 
  Alert,
  Badge,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardActionArea,
  CardMedia,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import PartialLinePayField from './common/PartialLinePayField';
import {
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  Home as HomeIcon,
  ShoppingCart as ShoppingCartIcon,
  TapAndPlay as TapAndPlayIcon,
  QrCode2 as QrCode2Icon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import PageLayout from './common/PageLayout';
import SessionExpiryDialog from './common/SessionExpiryDialog';
import ZelleDialog from './ZelleDialog';

// Animation for pointing down to indicate terminal location
const pointDownAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
    opacity: 1;
  }
  50% {
    transform: translateY(10px);
    opacity: 0.7;
  }
`;

// Animation for hand moving up and down to draw attention
const handBounceAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-12px);
  }
  50% {
    transform: translateY(0);
  }
  75% {
    transform: translateY(12px);
  }
`;

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, resetTimer, setSession, getSession } = useSession();

  // Manual card entry removed; Stripe.js Elements not used on this page
  
  // Payment state
  const [paymentMethod, setPaymentMethod] = useState(null); // 'swipe' | 'zelle' | null
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'error', or null
  const [paymentError, setPaymentError] = useState(null);
  const [statusHint, setStatusHint] = useState('');
  const [currentPaymentIntentId, setCurrentPaymentIntentId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  // Guard to prevent duplicate Zelle order creation
  const isCreatingOrderRef = useRef(false);
  // Zelle guidance dialog state (must be before any early returns)
  const [zelleDialogOpen, setZelleDialogOpen] = useState(false);
  // Countdown / deadline for processing
  const [processingDeadline, setProcessingDeadline] = useState(null);
  const processingDeadlineRef = useRef(null);
  const zelleRefCodeRef = useRef('');
  const cancelZellePendingRef = useRef(() => {});
  const zellePollIntervalRef = useRef(null);
  // Card flow guards to prevent completion after user-abort race conditions.
  const swipeAbortRequestedRef = useRef(false);
  const pendingCcOrderIdRef = useRef(null);
  const zelleCompletedSuccessfullyRef = useRef(false);
  const zelleDeadlineRef = useRef(null);
  const zelleGraceUntilRef = useRef(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const { cart: cartContext, clearCart, updateItemPayLineTotal, clearItemPayLineTotal } = useCart();
  const kb = useKeyboard();
  const [guestWantsReceipt, setGuestWantsReceipt] = useState(false);
  const [guestReceiptFirstName, setGuestReceiptFirstName] = useState('');
  const [guestReceiptLastName, setGuestReceiptLastName] = useState('');
  const [guestReceiptEmail, setGuestReceiptEmail] = useState('');

  const hasIncompletePartial = useMemo(
    () => Array.isArray(cartContext) && cartContext.some((i) => isPartialLineAmountUnset(i)),
    [cartContext]
  );
  // Global session inactivity timeout (for warning/expire + PI cleanup)
  const { registerActivePaymentIntent, clearActivePaymentIntent, noteActivity, start, cancel, suppressWarnings } = useSessionTimeout() || {};

  // Determine origin for navigation context
  const origin = (location?.state && location.state.from) || session?.lastOrigin || 'products';
  const originPath = origin === 'kotilinga' ? '/kotilinga' : '/shop';

  // Configurable Terminal payment timeout (ms), default 2 minutes
  // Separate from session timeout - this is specifically for waiting for card on terminal
  const terminalTimeoutMs = useMemo(() => {
    const raw = process.env.REACT_APP_TERMINAL_PAYMENT_TIMEOUT_MS || '120000';
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 120000;
  }, []);

  // Session warning/extend duration (ms), default 30 seconds
  const sessionExtendMs = useMemo(() => {
    const raw = process.env.REACT_APP_SESSION_EXTEND_MS || '30000';
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 30000;
  }, []);

  // Ensure global session timer is started when landing on PaymentPage (even if Zelle not used)
  useEffect(() => {
    try {
      if (typeof start === 'function') {
        start();
        console.log('[SessionTimeout][PaymentPage] Started timers on mount for /payment');
      }
    } catch {}
    // Cleanup: clear terminal when component unmounts ONLY if payment is still in progress
    return () => {
      // Only clear terminal if payment is actively processing (not succeeded/completed)
      // After successful payment, terminal is already idle, so no need to clear
      if (isProcessing && paymentStatus !== 'success' && paymentStatus !== 'completed' && currentPaymentIntentId) {
        clearTerminal(session?.kioskId || null).catch(e => {
          // Ignore errors - terminal might already be idle or busy
          if (e?.code !== 'terminal_reader_busy' && !e?.message?.includes('busy')) {
            console.warn('[PaymentPage] Cleanup terminal clear failed (ignored):', e?.message);
          }
        });
      }
    };
  }, [start, isProcessing, paymentStatus, currentPaymentIntentId, session?.kioskId]);

  // Local fallback session timeout on PaymentPage: 60s idle -> show warning for 30s -> navigate home
  const [localWarnOpen, setLocalWarnOpen] = useState(false);
  const [localSeconds, setLocalSeconds] = useState(30);
  const localTimersRef = useRef({ warnT: null, expireT: null, tickI: null });

  const clearLocalTimers = useCallback(() => {
    const t = localTimersRef.current;
    if (t.warnT) { clearTimeout(t.warnT); t.warnT = null; }
    if (t.expireT) { clearTimeout(t.expireT); t.expireT = null; }
    if (t.tickI) { clearInterval(t.tickI); t.tickI = null; }
  }, []);

  const scheduleLocalTimeout = useCallback(() => {
    clearLocalTimers();
    setLocalWarnOpen(false);
    // 60s idle -> open warning
    localTimersRef.current.warnT = setTimeout(() => {
      setLocalWarnOpen(true);
      setLocalSeconds(30);
      try { console.log('[SessionTimeout][PaymentPage][Local] Warning opened after 60s idle'); } catch {}
      // start 30s countdown
      localTimersRef.current.tickI = setInterval(() => {
        setLocalSeconds(prev => {
          const next = Math.max(0, prev - 1);
          return next;
        });
      }, 1000);
      // auto-expire after 30s
      localTimersRef.current.expireT = setTimeout(async () => {
        try { console.log('[SessionTimeout][PaymentPage][Local] Expiring and navigating to /home'); } catch {}
        clearLocalTimers();
        setLocalWarnOpen(false);
        // Only touch Stripe/terminal when user was in card flow (not Zelle)
        if (pmRef.current === 'swipe') {
          try {
            await clearTerminal(session?.kioskId || null);
            console.log('[PaymentPage][Local Timeout] Terminal cleared');
          } catch (e) {
            console.warn('[PaymentPage][Local Timeout] Terminal clear failed:', e?.message);
          }
          if (currentPaymentIntentId) {
            try {
              await cancelPaymentIntent(currentPaymentIntentId);
            } catch (e) {
              console.warn('[PaymentPage][Local Timeout] PI cancel failed:', e?.message);
            }
            try { clearActivePaymentIntent?.(); } catch {}
          }
        }
        // Cancel pending Zelle order if user had opened Zelle
        const zelleCode = zelleRefCodeRef.current;
        if (zelleCode) {
          try {
            await cancelZellePendingRef.current(zelleCode);
          } catch (e) {
            console.warn('[PaymentPage][Local Timeout] Zelle cancel failed:', e?.message);
          }
        }
        navigate('/home', { replace: true });
      }, 30000);
    }, 60000);
  }, [clearLocalTimers, navigate, session?.kioskId, currentPaymentIntentId, clearActivePaymentIntent]);

  // Start local timeout when on PaymentPage and not in Zelle dialog or processing
  useEffect(() => {
    // Only when not processing, no Zelle dialog
    if (isProcessing || (zelleDialogOpen && paymentMethod === 'zelle')) {
      clearLocalTimers();
      setLocalWarnOpen(false);
      return;
    }
    scheduleLocalTimeout();
    try { console.log('[SessionTimeout][PaymentPage][Local] Scheduled local idle timer'); } catch {}
    return () => clearLocalTimers();
  }, [isProcessing, zelleDialogOpen, paymentMethod, scheduleLocalTimeout, clearLocalTimers]);

  // Reset local idle timer on user activity within PaymentPage
  useEffect(() => {
    const onAny = () => {
      if (localWarnOpen) return; // don't auto-reset while dialog is open
      scheduleLocalTimeout();
    };
    document.addEventListener('click', onAny, { passive: true });
    document.addEventListener('touchstart', onAny, { passive: true });
    document.addEventListener('keydown', onAny, { passive: true });
    return () => {
      document.removeEventListener('click', onAny);
      document.removeEventListener('touchstart', onAny);
      document.removeEventListener('keydown', onAny);
    };
  }, [localWarnOpen, scheduleLocalTimeout]);

  // Zelle window: 5 minutes; show warning only in the final minute.
  const zelleWindowMs = 300000; // 5 minutes
  const zelleWarningStartMs = 60000; // show warning when <= 1 min left
  const zelleExtensionMs = 300000; // one extra 5-minute extension
  const zelleGraceMs = 60000; // final 60-second grace window after timer reaches zero
  const [zelleDeadline, setZelleDeadline] = useState(null);
  const [zelleRemainingMs, setZelleRemainingMs] = useState(0);
  const [zelleShowWarning, setZelleShowWarning] = useState(false);
  // After the timer ends, keep dialog for a short grace window.
  const [zelleGraceActive, setZelleGraceActive] = useState(false);
  const [zelleGraceUntil, setZelleGraceUntil] = useState(null);
  const [zelleExtensionUsed, setZelleExtensionUsed] = useState(false);

  // Simple hint: after 15s with no selection, show an inline alert once; hide when a method is chosen
  const [selectMethodHintOpen, setSelectMethodHintOpen] = useState(false);
  const pmRef = useRef(paymentMethod);
  useEffect(() => { pmRef.current = paymentMethod; }, [paymentMethod]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!pmRef.current) setSelectMethodHintOpen(true);
    }, 15000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (paymentMethod) setSelectMethodHintOpen(false);
  }, [paymentMethod]);

  // Initialize Zelle 5-minute window and tick remaining time (respects extensions).
  // Use refs so the interval always sees latest deadline/grace; avoids warning not showing due to stale closure.
  useEffect(() => {
    if (!(zelleDialogOpen && paymentMethod === 'zelle')) {
      zelleDeadlineRef.current = null;
      zelleGraceUntilRef.current = null;
      setZelleDeadline(null);
      setZelleRemainingMs(0);
      setZelleShowWarning(false);
      setZelleGraceActive(false);
      setZelleGraceUntil(null);
      setZelleExtensionUsed(false);
      return;
    }
    // Establish deadline if not set (first open)
    if (zelleDeadlineRef.current == null) {
      const initial = Date.now() + zelleWindowMs;
      zelleDeadlineRef.current = initial;
      setZelleDeadline(initial);
      setZelleRemainingMs(zelleWindowMs);
      setZelleShowWarning(false);
    }
    const interval = setInterval(() => {
      const now = Date.now();
      const graceUntil = zelleGraceUntilRef.current;
      if (graceUntil != null) {
        // During grace, count down grace window
        const grem = Math.max(0, graceUntil - now);
        setZelleRemainingMs(grem);
        setZelleShowWarning(true);
        if (grem <= 0) {
          zelleGraceUntilRef.current = null;
          const code = zelleRefCodeRef.current;
          if (code) cancelZellePendingRef.current(code);
          setZelleDialogOpen(false);
        }
        return;
      }

      const dl = zelleDeadlineRef.current;
      if (dl == null) return;
      const rem = Math.max(0, dl - now);
      setZelleRemainingMs(rem);
      setZelleShowWarning(rem <= zelleWarningStartMs);
      if (rem <= 0) {
        // Start a final grace window instead of closing immediately.
        const graceEnd = now + zelleGraceMs;
        zelleGraceUntilRef.current = graceEnd;
        zelleDeadlineRef.current = null;
        setZelleGraceActive(true);
        setZelleGraceUntil(graceEnd);
        setZelleRemainingMs(zelleGraceMs);
        setZelleShowWarning(true);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [zelleDialogOpen, paymentMethod, zelleGraceMs, zelleWarningStartMs, zelleWindowMs]);

  // While Zelle dialog is open, suppress the global session warning UI; otherwise use configured default timers
  useEffect(() => {
    if (!start) return;
    if (zelleDialogOpen && paymentMethod === 'zelle') {
      // Suppress session warning dialog when Zelle guidance is active
      suppressWarnings?.(true);
      cancel?.();
    } else {
      // Revert to default configured timeout
      suppressWarnings?.(false);
      start();
    }
  }, [zelleDialogOpen, paymentMethod, start, cancel, suppressWarnings]);

  // Zelle extend handler: allow one 5-minute extension and restart the dialog countdown.
  const handleZelleExtend = useCallback(() => {
    if (zelleExtensionUsed) return;
    const now = Date.now();
    const newDeadline = now + zelleExtensionMs;
    zelleDeadlineRef.current = newDeadline;
    zelleGraceUntilRef.current = null;
    setZelleDeadline(newDeadline);
    setZelleGraceActive(false);
    setZelleGraceUntil(null);
    setZelleRemainingMs(zelleExtensionMs);
    setZelleShowWarning(false);
    setZelleExtensionUsed(true);
    if (start) start(zelleExtensionMs);
  }, [start, zelleExtensionMs, zelleExtensionUsed]);

  // Tick remaining time while processing
  useEffect(() => {
    if (!isProcessing) return;
    const id = setInterval(() => {
      const dl = processingDeadlineRef.current;
      if (!dl) return;
      setRemainingMs(Math.max(0, dl - Date.now()));
    }, 500);
    return () => clearInterval(id);
  }, [isProcessing]);

  // Flat fee amount (for totals <= $10)
  const flatFeeAmount = useMemo(() => {
    const raw = parseFloat(process.env.REACT_APP_FLAT_FEE || '1');
    return Number.isFinite(raw) ? raw : 1;
  }, []);

  // Display percentage for transaction fees from env (e.g., 2.5)
  const feePercentDisplay = useMemo(() => {
    const n = parseFloat(process.env.REACT_APP_CREDIT_CARD_FEE_PERCENTAGE || '2.5');
    return Number.isFinite(n) ? n : 2.5;
  }, []);

  // Calculate order summary with error handling
  const orderSummary = useMemo(() => {
    const summary = {
      subtotal: 0,
      tax: 0,
      fee: 0,
      total: 0,
      items: []
    };

  // Cancel terminal flow and return to /shop (keep cart intact)
  const handleCancelAndReturnShop = async () => {
    if (isCancelling) return;
    try {
      setIsCancelling(true);
      if (paymentMethod === 'swipe') {
        try {
          await clearTerminal(session?.kioskId || null);
          console.log('[PaymentPage] Terminal cleared in handleCancelAndReturnShop');
        } catch (e) {
          console.warn('[PaymentPage] Terminal clear failed (ignored):', e?.message);
        }
        if (currentPaymentIntentId) {
          try { await cancelPaymentIntent(currentPaymentIntentId); } catch {}
          try { clearActivePaymentIntent?.(); } catch {}
        }
      }
      setCurrentPaymentIntentId(null);
      setStatusHint('');
      setPaymentStatus(null);
      setPaymentError(null);
      setIsProcessing(false);
      navigate('/shop', { replace: true });
    } finally {
      setIsCancelling(false);
    }
  };

    const currentPaymentMethod = paymentMethod || null;

    if (!Array.isArray(cartContext) || cartContext.length === 0) {
      return summary;
    }

    try {
      // Calculate subtotal from cart items
      const subtotal = cartContext.reduce((sum, item) => sum + getLineChargeTotal(item), 0);

      // Calculate tax (if applicable)
      const taxRate = 0; // Set your tax rate here
      const tax = subtotal * taxRate;
      
      // Calculate card processing fee only when using card/terminal flow
      let fee = 0;
      let feePercentage = null;
      let flatFee = null;
      let flatFeeApplied = false;
      if (currentPaymentMethod === 'swipe' || currentPaymentMethod === 'credit-card') {
        feePercentage = parseFloat(process.env.REACT_APP_CREDIT_CARD_FEE_PERCENTAGE || '2.5') / 100;
        const flatFeeRaw = parseFloat(process.env.REACT_APP_FLAT_FEE || '1');
        flatFee = Number.isFinite(flatFeeRaw) ? flatFeeRaw : 1;
        flatFeeApplied = subtotal <= 10;
        fee = parseFloat(((flatFeeApplied ? flatFee : subtotal * feePercentage)).toFixed(2));
      }

      // Calculate total including tax (fee for card only)
      const total = subtotal + tax + fee;

      // Log the calculated amounts for debugging
      console.log('Order Summary Calculation:', {
        subtotal: parseFloat(subtotal.toFixed(2)),
        tax: parseFloat(tax.toFixed(2)),
        fee: parseFloat(fee.toFixed(2)),
        total: total,
        feePercentage: feePercentage != null ? (feePercentage * 100 + '%') : 'n/a',
        flatFeeApplied,
        flatFee: flatFee != null ? flatFee : 'n/a'
      });

      return {
        subtotal: parseFloat(subtotal.toFixed(2)),
        tax: parseFloat(tax.toFixed(2)),
        fee: parseFloat(fee.toFixed(2)),
        total: total,
        items: [...cartContext] // Create a copy of cart items
      };
    } catch (error) {
      console.error('Error calculating order summary:', error);
      return summary;
    }
  }, [cartContext, paymentMethod]);

  /**
   * Donation Summary donor row: one-line "ICCT Devotee", no email.
   * True when: no user id, cart said guest, or session is the generic kiosk donor name.
   */
  const isGuestPaymentContext = useMemo(() => {
    if (location.state?.isGuestCheckout === true) return true;
    if (!session?.user?.id) return true;
    const fn = (session.user.first_name || '').trim();
    const ln = (session.user.last_name || '').trim();
    const combined = `${fn} ${ln}`.trim().toLowerCase();
    if (combined === 'icct devotee') return true;
    return false;
  }, [location.state, session?.user]);

  const buildPaymentCustomer = useCallback(() => {
    if (!isGuestPaymentContext) {
      return {
        ...session?.user,
        customer_id: session?.user?.customer_id || process.env.REACT_APP_GUEST_CHECKOUT_USER_ID
      };
    }
    return buildGuestOrderCustomer(session?.user, {
      wantsReceipt: guestWantsReceipt,
      receiptEmail: guestReceiptEmail,
      firstName: guestReceiptFirstName,
      lastName: guestReceiptLastName
    });
  }, [
    isGuestPaymentContext,
    session?.user,
    guestWantsReceipt,
    guestReceiptEmail,
    guestReceiptFirstName,
    guestReceiptLastName
  ]);

  const assertGuestReceiptValid = useCallback(() => {
    const result = validateGuestReceiptInput(isGuestPaymentContext, guestWantsReceipt, {
      email: guestReceiptEmail,
      firstName: guestReceiptFirstName,
      lastName: guestReceiptLastName
    });
    if (!result.ok) {
      throw new Error(result.message);
    }
  }, [
    isGuestPaymentContext,
    guestWantsReceipt,
    guestReceiptEmail,
    guestReceiptFirstName,
    guestReceiptLastName
  ]);

  const isCardPaymentSelected = paymentMethod === 'swipe';
  const isZellePaymentSelected = paymentMethod === 'zelle';

  const guestReceiptKbOpts = {
    position: 'below',
    widthPct: 0.85,
    heightVh: 35,
    showEnter: true,
    autoHeight: true,
    anchorOffsetPx: 100,
    hCenter: true
  };

  const openGuestReceiptFirstNameKeyboard = useCallback(() => {
    if (!kb || kb.keyboardDisabled) return;
    kb.open('alpha', guestReceiptKbOpts, {
      onInput: (ch) => setGuestReceiptFirstName((prev) => (prev || '') + ch),
      onBackspace: () => setGuestReceiptFirstName((prev) => (prev || '').slice(0, -1)),
      onEnter: () => {
        try {
          kb.close();
        } catch {}
      },
      onClear: () => setGuestReceiptFirstName('')
    });
  }, [kb]);

  const openGuestReceiptLastNameKeyboard = useCallback(() => {
    if (!kb || kb.keyboardDisabled) return;
    kb.open('alpha', guestReceiptKbOpts, {
      onInput: (ch) => setGuestReceiptLastName((prev) => (prev || '') + ch),
      onBackspace: () => setGuestReceiptLastName((prev) => (prev || '').slice(0, -1)),
      onEnter: () => {
        try {
          kb.close();
        } catch {}
      },
      onClear: () => setGuestReceiptLastName('')
    });
  }, [kb]);

  const openGuestReceiptEmailKeyboard = useCallback(() => {
    if (!kb || kb.keyboardDisabled) return;
    kb.open('email', guestReceiptKbOpts, {
      onInput: (ch) => setGuestReceiptEmail((prev) => (prev || '') + ch),
      onBackspace: () => setGuestReceiptEmail((prev) => (prev || '').slice(0, -1)),
      onEnter: () => {
        try {
          kb.close();
        } catch {}
      },
      onClear: () => setGuestReceiptEmail('')
    });
  }, [kb]);

  // Robust amount fallback for Zelle dialog/polling
  const zelleAmount = useMemo(() => {
    const osTotal = parseFloat((orderSummary?.total ?? 0).toFixed(2));
    if (osTotal > 0) return osTotal;
    // Fallback 1: compute subtotal directly from cart
    try {
      const subtotal = Array.isArray(cartContext)
        ? cartContext.reduce((sum, item) => sum + getLineChargeTotal(item), 0)
        : 0;
      if (subtotal > 0) return parseFloat(subtotal.toFixed(2));
    } catch {}
    // Fallback 2: use session current order if present
    const so = parseFloat(session?.currentOrder?.total || 0);
    return Number.isFinite(so) && so > 0 ? so : 0;
  }, [orderSummary.total, cartContext, session, buildPaymentCustomer]);

  // Zelle: 6-digit reference code and polling interval
  const [zelleRefCode, setZelleRefCode] = useState('');
  const [zelleSentBy, setZelleSentBy] = useState('');
  const [zelleWaiting, setZelleWaiting] = useState(false);
  const [zelleConfirmed, setZelleConfirmed] = useState(false);
  const [zelleFailed, setZelleFailed] = useState(false);
  const [zelleErrorMessage, setZelleErrorMessage] = useState('');
  const zellePollMs = useMemo(() => {
    const raw = parseInt(process.env.REACT_APP_ZELLE_POLL_INTERVAL_MS || '5000', 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 5000;
  }, []);

  const ZELLE_GENERATE_RETRIES = 3;
  const ZELLE_GENERATE_RETRY_DELAY_MS = 1000;

  const generateZelleCode = useCallback(async () => {
    const totalAmount = parseFloat(orderSummary.total.toFixed(2));
    const amountCents = Math.round(totalAmount * 100);
    const cartData = cartContext.map(item => ({
      product_id: item.product_id,
      variation_id: item.variation_id || 0,
      quantity: item.quantity || 1,
      price: item.price,
      name: item.name
    }));
    const customerData = session?.user ? buildPaymentCustomer() : null;
    let lastError;
    for (let attempt = 1; attempt <= ZELLE_GENERATE_RETRIES; attempt++) {
      try {
        const response = await post('/api/zelle/generate-code', {
          kioskId: session?.kioskId || null,
          amountCents,
          cartData,
          customerData
        });
        const code = response.memo_code;
        setZelleRefCode(code);
        console.log(`[Zelle] Generated memo code ${code} (attempt ${attempt}) with pending_order_id: ${response.pending_order_id}`);
        return code;
      } catch (error) {
        lastError = error;
        console.warn(`[Zelle] Generate code attempt ${attempt}/${ZELLE_GENERATE_RETRIES} failed:`, error?.message || error);
        if (attempt < ZELLE_GENERATE_RETRIES) {
          await new Promise(r => setTimeout(r, ZELLE_GENERATE_RETRY_DELAY_MS));
        }
      }
    }
    console.error('Failed to generate Zelle code after retries:', lastError);
    throw lastError;
  }, [orderSummary.total, cartContext, buildPaymentCustomer, session?.kioskId]);

  const handleStartZellePayment = useCallback(async () => {
    try {
      assertGuestReceiptValid();
      setPaymentError(null);
      setPaymentStatus(null);
      await generateZelleCode();
      setZelleDialogOpen(true);
      setZelleConfirmed(false);
      setZelleFailed(false);
      setZelleErrorMessage('');
    } catch (e) {
      setPaymentStatus('error');
      setPaymentError(e.message || 'Unable to process Zelle payments. Please try another payment method.');
    }
  }, [assertGuestReceiptValid, generateZelleCode]);

  const cancelZellePending = useCallback(async (memoCode) => {
    if (!memoCode) return;
    try {
      await put('/api/zelle/cancel-pending', { memo_code: memoCode });
    } catch (e) {
      console.warn('[Zelle] Cancel pending failed:', e?.message);
    }
    setZelleRefCode('');
  }, []);

  useEffect(() => { zelleRefCodeRef.current = zelleRefCode; }, [zelleRefCode]);
  useEffect(() => { cancelZellePendingRef.current = cancelZellePending; }, [cancelZellePending]);

  // On unmount (navigate away, etc.), cancel pending Zelle order so it is not left pending
  useEffect(() => {
    return () => {
      const code = zelleRefCodeRef.current;
      if (code && !zelleCompletedSuccessfullyRef.current) {
        put('/api/zelle/cancel-pending', { memo_code: code }).catch(() => {});
      }
    };
  }, []);

  // Poll DB only (GET order-status). Webhook updates record; frontend never calls Gmail.
  // Stop polling as soon as we get completed/failed/cancelled so we don't keep hitting the API.
  useEffect(() => {
    if (paymentMethod !== 'zelle' || !zelleRefCode || !zelleDialogOpen || zelleFailed) return;
    let cancelled = false;
    zellePollIntervalRef.current = null;

    const stopPolling = () => {
      if (zellePollIntervalRef.current) {
        clearInterval(zellePollIntervalRef.current);
        zellePollIntervalRef.current = null;
      }
    };

    const poll = async () => {
      try {
        const resp = await get(`/api/zelle/order-status?memo_code=${encodeURIComponent(zelleRefCode)}`);
        if (cancelled || !resp) return;
        if (resp.status === 'completed') {
          // Payment confirmed by the Gmail match. The Woo order is created out-of-band by the admin
          // app, so woo_order_id may be null here — success is based on the payment, not the order.
          stopPolling();
          setZelleWaiting(false);
          setZelleSentBy(resp.sent_by || '');
          await handleZelleSuccess(resp.sent_by || '', resp.woo_order_id || null);
          return;
        }
        // Cancelled, failed, or any error_message: show message and stop polling
        if (resp.status === 'cancelled' || resp.status === 'failed' || (resp.error_message && String(resp.error_message).trim())) {
          stopPolling();
          const msg = (resp.error_message && String(resp.error_message).trim())
            ? String(resp.error_message).trim()
            : resp.status === 'cancelled'
              ? 'This payment was cancelled.'
              : 'Payment could not be completed.';
          setZelleErrorMessage(msg);
          setZelleFailed(true);
          setZelleWaiting(false);
        }
      } catch (_) {
        // 404 or network error – keep polling
      }
    };

    setZelleWaiting(true);

    // First poll after 1s, then poll every zellePollMs
    poll();
    zellePollIntervalRef.current = setInterval(poll, zellePollMs);

    return () => {
      cancelled = true;
      stopPolling();
      setZelleWaiting(false);
    };
  }, [paymentMethod, zelleRefCode, zelleDialogOpen, zelleFailed, zellePollMs]);

  // Zelle order is created by webhook only. Frontend just navigates to confirmation when DB shows completed + woo_order_id.
  const handleZelleSuccess = useCallback(async (senderArg, existingWooOrderId) => {
    if (isCreatingOrderRef.current) return;
    isCreatingOrderRef.current = true;
    zelleCompletedSuccessfullyRef.current = true; // so unmount cleanup does NOT call cancel-pending
    const order = {
      id: existingWooOrderId || undefined, // may be null (admin app creates it out-of-band)
      status: 'completed',
      payment_method: 'zelle',
      payment_method_title: 'Zelle',
      total: orderSummary.total.toFixed(2)
    };
    setSession(prev => ({
      ...prev,
      currentOrder: order,
      currentOrderId: existingWooOrderId || null,
      orderStatus: 'completed',
      orderDate: new Date().toISOString(),
      paymentStatus: 'completed',
      paymentMethod: 'zelle',
      transactionId: `zelle_${zelleRefCode}`
    }));
    setZelleConfirmed(true);
    setTimeout(() => {
      navigate('/confirmation', {
        state: { order, payment: { id: `zelle_${zelleRefCode}`, status: 'succeeded', amount: Math.round(parseFloat(orderSummary.total.toFixed(2)) * 100) } },
        replace: true
      });
    }, 1000);
  }, [orderSummary.total, setSession, zelleRefCode, navigate]);

  // Cancel reader action (best-effort) and any active PaymentIntent, then return home
  const handleCancelAndReturnHome = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      if (paymentMethod === 'swipe') {
        try {
          await clearTerminal(session?.kioskId || null);
          console.log('[PaymentPage] Terminal cleared successfully');
        } catch (e) {
          console.warn('[PaymentPage] Terminal clear failed (ignored):', e?.message);
        }
        if (currentPaymentIntentId) {
          try {
            await cancelPaymentIntent(currentPaymentIntentId);
          } catch (e) {
            console.warn('Cancel PaymentIntent failed (ignored):', e?.message);
          }
          try { clearActivePaymentIntent?.(); } catch {}
        }
      }
      // Reset local state
      setCurrentPaymentIntentId(null);
      setStatusHint('');
      setPaymentStatus(null);
      setPaymentError(null);
      setIsProcessing(false);
      // Clear cart and go home
      try { await clearCart(); } catch {}
      navigate('/', { replace: true });
    } finally {
      setIsCancelling(false);
    }
  };

  // Process payment and create WooCommerce order
  const processPayment = useCallback(async () => {
    setIsProcessing(true);
    setPaymentError(null);
    
    try {
      if (!Array.isArray(cartContext) || cartContext.length === 0) {
        throw new Error('Cart is empty. Please add items to your cart before proceeding with payment.');
      }

      // Ensure the total is properly rounded to 2 decimal places
      const total = parseFloat(orderSummary.total.toFixed(2));
      
      // Log the amount being used for the order
      console.log('Creating order with amount:', {
        subtotal: orderSummary.subtotal,
        tax: orderSummary.tax,
        fee: orderSummary.fee,
        total: total,
        totalInCents: Math.round(total * 100)
      });

      // Create order data
      const orderData = {
        cartItems: cartContext,
        customer: buildPaymentCustomer(),
        payment_method: paymentMethod === 'credit-card' ? 'stripe_cc' : 'swipe',
        payment_method_title: paymentMethod === 'credit-card' ? 'Credit Card' : 'Swipe',
        status: 'pending',
        total: total // Ensure we're using the properly calculated total
      };
      
      // Create the order and store in session
      const order = await createWooCommerceOrder({
        ...orderData,
        kioskId: session?.kioskId,
        fee: orderSummary.fee,
        total: total,
        setSession: setSession,
        isGuestCheckout: isGuestPaymentContext,
        guestReceiptEmail: isGuestPaymentContext && guestWantsReceipt ? guestReceiptEmail : undefined
      });
      
      if (!order || !order.id) {
        throw new Error('Failed to create order: Invalid response from server');
      }
      
      console.log('Order created successfully:', {
        orderId: order.id,
        orderNumber: order.order_number || `#${order.id}`,
        total: total,
        totalInCents: Math.round(total * 100)
      });
      
      return {
        ...order,
        success: true,
        order_number: order.order_number || `#${order.id}`,
        order_key: order.order_key || '',
        status: order.status || 'processing',
        total: total, // Use the properly calculated total
        items: cartContext,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Error creating order:', error);
      setPaymentError(error.message || 'Failed to create order. Please try again.');
      setPaymentStatus('error');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [cartContext, orderSummary.total, paymentMethod, session, buildPaymentCustomer, isGuestPaymentContext, guestWantsReceipt, guestReceiptEmail]);
  
  
  // Handle return to home
  const handleReturnHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Handle payment submission for swipe payments
  const handlePaymentSubmit = async () => {
    if (isProcessing) return;
    if (hasIncompletePartial) {
      setPaymentStatus('error');
      setPaymentError('Please enter the initial payment amount for each highlighted line, then try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    try {
      assertGuestReceiptValid();
    } catch (e) {
      setPaymentStatus('error');
      setPaymentError(e.message || 'Please enter a valid email for your receipt.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    try {
      setIsProcessing(true);
      setPaymentStatus('processing');
      setPaymentError(null);
      
      if (paymentMethod === 'swipe') {
        const result = await handleSwipePayment();
        if (!result?.success) {
          throw new Error(result?.error || 'Payment failed');
        }
        // Don't set isProcessing to false here - handleSwipePayment will handle navigation
        return;
      }
      // For credit card, the Stripe form handles everything
      // and will handle its own navigation and error states
      
    } catch (error) {
      console.error('Payment submission error:', error);
      setPaymentStatus('error');
      setPaymentError(error.message || 'Failed to process payment');
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSwipePayment = async () => {
  let kioskCcOrderId = null;
  let paymentSucceeded = false;
  try {
    swipeAbortRequestedRef.current = false;
    pendingCcOrderIdRef.current = null;
    setIsProcessing(true);
    setPaymentStatus('processing');
    setPaymentError(null);
    setStatusHint('Preparing order...');
    assertGuestReceiptValid();
    const total = parseFloat(orderSummary.total.toFixed(2));
    const customerData = buildPaymentCustomer();
    const orderData = {
      cartItems: cartContext.map((item) => ({
        ...item,
        price: item.price,
        total: getLineChargeTotal(item).toFixed(2)
      })),
      customer: customerData,
      payment_method: 'swipe',
      payment_method_title: 'Swipe',
      total
    };
    // Create pending CC record in kiosk_cc_orders before PaymentIntent (required for complete/fail)
    console.log('[Terminal] Creating pending CC order (before PaymentIntent)...');
    const pendingOrder = await createWooCommerceOrder({
      ...orderData,
      kioskId: session?.kioskId,
      fee: orderSummary.fee,
      total,
      setSession,
      isGuestCheckout: isGuestPaymentContext,
      guestReceiptEmail: isGuestPaymentContext && guestWantsReceipt ? guestReceiptEmail : undefined
    });
    if (!pendingOrder?.id) throw new Error('Failed to create pending order');
    kioskCcOrderId = pendingOrder.id;
    pendingCcOrderIdRef.current = kioskCcOrderId;
    console.log('[Terminal] Pending CC order created id=%s', kioskCcOrderId);

    setStatusHint('Waiting for card. Please tap/insert/swipe on the terminal...');
    const nowStart = Date.now();
    if (!processingDeadlineRef.current) {
      const dl = nowStart + terminalTimeoutMs;
      processingDeadlineRef.current = dl;
      setProcessingDeadline(dl);
      setRemainingMs(dl - nowStart);
    }

    // Create PaymentIntent only when card dialog opens (user has started terminal flow).
    // Cancel when: dialog closed (X button), dialog timeout, user switches to Zelle, back to shop, or home.
    const stripeReceipt = getGuestStripeReceiptFields(customerData);
    const donorLabel =
      stripeReceipt.billing_first_name && stripeReceipt.billing_last_name
        ? `${stripeReceipt.billing_first_name} ${stripeReceipt.billing_last_name}`.trim()
        : null;
    const createResp = await createTerminalPaymentIntent({
      amount: total,
      currency: 'usd',
      description: donorLabel
        ? `Kiosk — ${donorLabel}`
        : `Kiosk payment for ${cartContext.length} items`,
      metadata: {
        customer_id: String(customerData?.customer_id || session?.user?.customer_id || 'guest'),
        customer_email: stripeReceipt.receipt_email || session?.user?.email || '',
        items_count: cartContext.length,
        source: 'kiosk_swipe',
        ...(stripeReceipt.billing_first_name
          ? { billing_first_name: stripeReceipt.billing_first_name }
          : {}),
        ...(stripeReceipt.billing_last_name ? { billing_last_name: stripeReceipt.billing_last_name } : {})
      },
      ...stripeReceipt
    });

    const paymentIntentId = createResp?.paymentIntentId;
    if (!paymentIntentId) throw new Error('Failed to create payment intent');
    setCurrentPaymentIntentId(paymentIntentId);
    // Register active PI for global session timeout cleanup
    try { registerActivePaymentIntent?.(paymentIntentId); } catch {}

    // 2) Ask backend to process on the configured reader
    await processPaymentIntentOnReader({ 
      payment_intent_id: paymentIntentId,
      kiosk_id: session?.kioskId || null
    });

    // 3) Poll PaymentIntent status until it is ready to capture or succeeded
    const start = Date.now();
    let paymentIntent;
    let lastStatus = null;
    while (true) {
      paymentIntent = await getPaymentIntentStatus(paymentIntentId);
      const status = paymentIntent?.status;
      console.log('[Terminal][FE] Poll status:', status, {
        id: paymentIntent?.id || paymentIntentId,
        error: paymentIntent?.error,
      });
      if (status && status !== lastStatus) {
        try { noteActivity?.(); } catch {}
        lastStatus = status;
      }
      // User closed the terminal dialog; do not finalize WC order unless payment already succeeded.
      if (
        swipeAbortRequestedRef.current &&
        status !== 'succeeded' &&
        status !== 'requires_capture'
      ) {
        throw new Error('Payment was canceled by user');
      }
      if (status === 'error' || status === 'invalid_id') {
        throw new Error(paymentIntent?.error || 'Failed to retrieve payment status');
      }
      // If Stripe reports a last_payment_error (e.g., card_declined), surface it immediately
      const lpe = paymentIntent?.last_payment_error;
      if (lpe) {
        const code = lpe.code || 'unknown_error';
        // Show a generic user-friendly message without technical details
        if (code === 'card_declined' || lpe.decline_code) {
          throw new Error('Your card was declined.');
        }
        throw new Error('Payment failed. Please try again.');
      }
      // Update user hint based on status
      switch (status) {
        case 'requires_payment_method':
          setStatusHint('Waiting for card. Please tap/insert/swipe on the terminal...');
          break;
        case 'requires_confirmation':
        case 'requires_action':
          setStatusHint('Confirm on the terminal to continue...');
          break;
        case 'requires_capture':
          setStatusHint('Processing payment... Please wait...');
          break;
        case 'processing':
          setStatusHint('Processing payment on the terminal...');
          break;
        case 'succeeded':
          setStatusHint('Payment successful! Finalizing order...');
          break;
        default:
          // Keep the last meaningful message instead of clearing it
          if (!statusHint || statusHint === '') {
            setStatusHint('Processing payment... Please wait...');
          }
      }
      if (status === 'requires_capture' || status === 'succeeded') break;
      // While the reader is waiting for a card, PI stays in requires_payment_method
      if (status === 'canceled') {
        throw new Error('Payment was canceled');
      }
      const deadline = processingDeadlineRef.current || (start + terminalTimeoutMs);
      if (Date.now() > deadline) { // Timeout
        // Clear terminal first, then cancel PI, then reset UI and go home
        try {
          await clearTerminal(session?.kioskId || null);
          console.log('[PaymentPage] Terminal cleared on timeout');
        } catch (e) {
          console.warn('Terminal clear on timeout failed (ignored):', e?.message);
        }
        try {
          await cancelPaymentIntent(paymentIntentId);
        } catch (e) {
          console.warn('PI cancel on timeout failed (ignored):', e?.message);
        }
        try { clearActivePaymentIntent?.(); } catch {}
        setCurrentPaymentIntentId(null);
        setStatusHint('');
        setPaymentStatus('error');
        setPaymentError('Timed out waiting for terminal. Payment attempt canceled.');
        try { await failCcOrder(kioskCcOrderId, { error_message: 'Terminal timeout' }); } catch (_) {}
        await clearCart();
        navigate('/', { replace: true });
        return { success: false, error: 'Timed out waiting for terminal' };
      }
      await new Promise(r => setTimeout(r, 2000));
    }

    // 4) Capture if required
    if (paymentIntent.status === 'requires_capture') {
      paymentIntent = await capturePaymentIntent(paymentIntentId);
    }

    if (paymentIntent.status !== 'succeeded') {
      throw new Error(`Unexpected payment status: ${paymentIntent.status}`);
    }
    // Payment is confirmed. Per the integration guide, from here success is based on the PAYMENT,
    // not on the order existing: we always land on the confirmation screen and never show a payment
    // error (or mark the order failed) for a downstream hand-off hiccup.
    paymentSucceeded = true;
    setStatusHint('Payment successful! Finalizing...');

    // 5) Hand the order to the backend, which verifies the PaymentIntent and enqueues it for
    //    out-of-band creation by the admin app. A hand-off failure does NOT fail the donor's
    //    payment — the charge is real and the local record + PaymentIntent id let it be reconciled.
    let completeRes = null;
    try {
      completeRes = await completeCcOrder(kioskCcOrderId, { payment_intent_id: paymentIntentId });
    } catch (handoffErr) {
      console.error('[Terminal][FE] order hand-off failed after successful payment (will be reconciled):', handoffErr?.message || handoffErr);
    }
    const wooOrderId = completeRes?.id || null;
    // No order number yet (queued) or the hand-off failed → confirmation shows "being recorded".
    const isQueued = !wooOrderId;

    // 6) Annotate Stripe PI with WooCommerce order details (best-effort). Skipped when queued —
    //    the queue stamps tx_id = PaymentIntent id and annotates the order out-of-band.
    if (wooOrderId) {
      try {
        const postPayStripe = getGuestStripeReceiptFields(customerData);
        const customerEmail = postPayStripe.receipt_email || session?.user?.email || '';
        const customerId = customerData?.customer_id || session?.user?.customer_id || 'guest';
        await updatePaymentIntentMetadata(paymentIntentId, {
          description: `Order #${wooOrderId} - ${customerEmail || 'kiosk order'}`,
          metadata: {
            wc_order_id: String(wooOrderId),
            ...(Array.isArray(completeRes.payment_order_ids) && completeRes.payment_order_ids.length
              ? { wc_payment_order_ids: completeRes.payment_order_ids.join(',') }
              : {}),
            ...(Array.isArray(completeRes.pledge_order_ids) && completeRes.pledge_order_ids.length
              ? { wc_pledge_order_ids: completeRes.pledge_order_ids.join(',') }
              : {}),
            customer_id: String(customerId),
            customer_email: customerEmail,
            kiosk_source: 'kiosk_terminal',
            ...(postPayStripe.billing_first_name
              ? { billing_first_name: postPayStripe.billing_first_name }
              : {}),
            ...(postPayStripe.billing_last_name
              ? { billing_last_name: postPayStripe.billing_last_name }
              : {})
          }
        });
      } catch (e) {
        console.warn('[Terminal][FE] Failed to update PI metadata (non-fatal):', e?.message);
      }
    }

    // 7) Build a display order (order number absent on the queued path until the worker creates it)
    //    and navigate. Session/confirmation reflect payment success, not Woo-order existence.
    const displayOrder = {
      id: wooOrderId || undefined,
      status: wooOrderId ? (completeRes.status || 'completed') : 'processing',
      payment_method: 'swipe',
      payment_method_title: 'Swipe',
      total: (completeRes?.total != null ? completeRes.total : total.toFixed(2)),
      line_items: orderData.cartItems,
      fee_lines: [{
        name: process.env.REACT_APP_CREDIT_CARD_FEE_NAME || 'Credit Card Processing Fee',
        total: (orderSummary.fee || 0).toFixed(2)
      }],
      billing: customerData?.billing,
      ...(Array.isArray(completeRes?.payment_order_ids) && completeRes.payment_order_ids.length
        ? { payment_order_ids: completeRes.payment_order_ids } : {}),
      ...(Array.isArray(completeRes?.pledge_order_ids) && completeRes.pledge_order_ids.length
        ? { pledge_order_ids: completeRes.pledge_order_ids } : {})
    };

    setSession(prev => ({
      ...prev,
      currentOrder: displayOrder,
      currentOrderId: wooOrderId || null,
      orderStatus: wooOrderId ? 'completed' : 'processing',
      orderDate: new Date().toISOString(),
      paymentStatus: 'completed',
      paymentMethod: 'card_swipe',
      transactionId: paymentIntentId
    }));
    try { clearActivePaymentIntent?.(); } catch {}

    navigate('/confirmation', {
      state: {
        order: displayOrder,
        queued: isQueued && !wooOrderId,
        paymentIntentId,
        payment: {
          id: paymentIntentId,
          status: 'succeeded',
          amount: paymentIntent.amount,
          created: paymentIntent.created
        }
      },
      replace: true
    });

    return { success: true };
  } catch (error) {
    console.error('Swipe payment error:', error);
    // Guide: success is based on the payment. Once the charge succeeded we never show a payment
    // error or mark the order failed — any post-payment throw is a recording concern, not the
    // donor's problem (the confirmation flow already handles "being recorded").
    if (paymentSucceeded) {
      return { success: true };
    }
    setPaymentStatus('error');
    setPaymentError(error.message || 'Failed to process payment');
    try {
      if (kioskCcOrderId != null) {
        await failCcOrder(kioskCcOrderId, { error_message: error.message });
      }
    } catch (_) {}
    return { success: false, error: error.message || 'Failed to process card swipe' };
  } finally {
    setIsProcessing(false);
    // keep currentPaymentIntentId on error so user can cancel; clear on success above via navigation
    swipeAbortRequestedRef.current = false;
    pendingCcOrderIdRef.current = null;
    setRemainingMs(0);
    setStatusHint('');
  }
};

  // Abort terminal processing and reset state (no navigation)
  const abortProcessingAndReset = useCallback(async () => {
    try {
      swipeAbortRequestedRef.current = true;
      // Clear terminal to stop waiting for card swipe
      try {
        await clearTerminal(session?.kioskId || null);
        console.log('[PaymentPage] Terminal cleared in abortProcessingAndReset');
      } catch (e) {
        console.warn('[PaymentPage] Terminal clear failed (ignored):', e?.message);
      }
      // Cancel PI best-effort
      if (currentPaymentIntentId) {
        try { await cancelPaymentIntent(currentPaymentIntentId); } catch {}
        try { clearActivePaymentIntent?.(); } catch {}
      }
      // Mark local pending CC record as cancelled so it cannot be completed later.
      if (pendingCcOrderIdRef.current != null) {
        try { await cancelCcOrder(pendingCcOrderIdRef.current, { error_message: 'User canceled payment' }); } catch {}
      }
    } finally {
      setIsProcessing(false);
      setCurrentPaymentIntentId(null);
      setStatusHint('');
      setPaymentStatus(null);
      setPaymentError(null);
    }
  }, [currentPaymentIntentId, session?.kioskId, clearActivePaymentIntent]);

  // Auto-close the terminal dialog after the configured terminalTimeoutMs
  useEffect(() => {
    if (!(isProcessing && paymentMethod === 'swipe')) return;
    const t = setTimeout(() => {
      abortProcessingAndReset();
    }, terminalTimeoutMs);
    return () => clearTimeout(t);
  }, [isProcessing, paymentMethod, terminalTimeoutMs, abortProcessingAndReset]);

  // Cancel current PaymentIntent to allow a clean retry
  const handleCancelCurrentPaymentIntent = async () => {
    if (!currentPaymentIntentId || isProcessing) return;
    try {
      setIsProcessing(true);
      await cancelPaymentIntent(currentPaymentIntentId);
      try { clearActivePaymentIntent?.(); } catch {}
      setPaymentStatus(null);
      setPaymentError(null);
      setCurrentPaymentIntentId(null);
    } catch (e) {
      console.error('Failed to cancel PaymentIntent:', e);
      setPaymentError(e?.message || 'Failed to cancel payment');
    } finally {
      setIsProcessing(false);
    }
  };


  // Function to update transaction progress
  const updateTransactionProgress = (updates) => {
    setTransactionDialog(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Show order details if payment was successful
  if (paymentStatus === 'success' && orderDetails) {
    return (
      <PageLayout contentMaxWidth="xl">
        <Box sx={{ mt: 0, mb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/checkout', { state: { fromPayment: true, cart: cartContext, from: origin }, replace: true })}
              disabled={isProcessing}
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
              }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/home')}
              disabled={isProcessing}
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
              }}
            >
              Home
            </Button>
          </Box>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/cart', { state: { from: origin } })}
              disabled={isProcessing}
              startIcon={
                <Badge badgeContent={cartContext?.length || 0} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              }
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
              }}
            >
              Cart
            </Button>
          </Box>
          <Box sx={{ flex: '0 0 auto', ml: 2 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelAndReturnHome}
              disabled={isCancelling}
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              {isCancelling ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Cancelling...
                </>
              ) : 'Cancel Order'}
            </Button>
          </Box>
        </Box>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 3 }} />
          <Typography variant="h4" gutterBottom>
            Order Confirmed!
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Thank you for your order
          </Typography>

          <Box sx={{ mt: 4, textAlign: 'left', maxWidth: 400, mx: 'auto', p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1, mb: 2 }}>
              Order Details
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">Order Number</Typography>
              <Typography variant="body1">{orderDetails.orderNumber}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">Status</Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{orderDetails.status}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">Total Amount</Typography>
              <Typography variant="body1">${parseFloat(orderDetails.total).toFixed(2)}</Typography>
            </Box>

            {orderDetails.orderNotes && (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                <Typography variant="subtitle2" color="text.secondary">Note:</Typography>
                <Typography variant="body2">{orderDetails.orderNotes}</Typography>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              // Clear the cart and session when starting a new order
              await clearCart();
              setPaymentStatus(null);
              setOrderDetails(null);
              // Removed automatic redirection to shop page
            }}
            sx={{ mt: 4 }}
          >
            Start New Order
          </Button>
        </Paper>
        </Box>
      </PageLayout>
    );
  }

  if (!Array.isArray(cartContext) || cartContext.length === 0) {
    return (
      <PageLayout contentMaxWidth="xl">
        <Box sx={{ mt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/checkout', { state: { fromPayment: true, cart: cartContext, from: origin }, replace: true })}
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
              }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/home')}
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
              }}
            >
              Home
            </Button>
          </Box>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/cart', { state: { from: origin } })}
              startIcon={
                <Badge badgeContent={cartContext?.length || 0} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              }
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
              }}
            >
              Cart
            </Button>
          </Box>
        </Box>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Your cart is empty
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Back to Menu
          </Button>
          {paymentStatus === 'error' && paymentError && (
            <Box sx={{ color: 'error.main', mt: 1, textAlign: 'center' }}>
              {paymentError}
            </Box>
          )}
        </Paper>
        </Box>
      </PageLayout>
    );
  }

  

  return (
    <PageLayout contentMaxWidth="xl">
      <Box sx={{ mt: 0, mb: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: '0 0 auto' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/checkout', { state: { fromPayment: true, cart: cartContext, from: origin }, replace: true })}
            disabled={isProcessing}
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: '#f3e5f5',
              borderColor: '#d1c4e9',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
            }}
          >
            Back
          </Button>
        </Box>
        <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/home')}
            disabled={isProcessing}
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: '#f3e5f5',
              borderColor: '#d1c4e9',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
            }}
          >
            Home
          </Button>
        </Box>
        <Box sx={{ flex: '0 0 auto' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/cart', { state: { from: origin } })}
            disabled={isProcessing}
            startIcon={
              <Badge badgeContent={cartContext?.length || 0} color="primary">
                <ShoppingCartIcon />
              </Badge>
            }
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: '#f3e5f5',
              borderColor: '#d1c4e9',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': { backgroundColor: '#e1bee7', borderColor: '#b39ddb' }
            }}
          >
            Cart
          </Button>
        </Box>
        <Box sx={{ flex: '0 0 auto', ml: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelAndReturnHome}
            disabled={isCancelling}
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            {isCancelling ? (
              <>
                <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                Cancelling...
              </>
            ) : 'Cancel Order'}
          </Button>
        </Box>
      </Box>

      

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        {/* Left Column: Dynamic Payment Flow */}
        <Box sx={{ flex: 2 }}>
          {paymentError && paymentStatus === 'error' && !paymentMethod && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => {
                setPaymentError(null);
                setPaymentStatus(null);
              }}
            >
              {paymentError}
            </Alert>
          )}
          {/* Donation Summary (line items + donor) — above Select Payment Method */}
          <Paper elevation={0} sx={{
            p: 3,
            mb: 3,
            position: 'relative',
            borderRadius: 2,
            background: 'linear-gradient(180deg, #f1f4fa 0%, #e8edf7 100%)',
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.08)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.08)'
          }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <MonetizationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
              Donation Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              {isGuestPaymentContext ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                  <PersonIcon color="action" fontSize="small" />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ICCT Devotee
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Donor
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
                      {session?.user?.first_name} {session?.user?.last_name || ''}
                    </Typography>
                  </Box>
                  {session?.user?.email && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                        {session.user.email.substring(0, 3)}
                        {`*`.repeat(5)}@
                        {session.user.email.split('@')[1]}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            {hasIncompletePartial && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Enter the initial payment for each line where the amount is missing, or tap the field to use the keypad.
              </Alert>
            )}
            <Divider sx={{ mb: 2 }} />
            {Array.isArray(cartContext) && cartContext.map((item) => {
              const idRaw = item.id || item.cart_item_id;
              const lineList = getLineListTotal(item);
              const showPartial = isPartialPaymentEligible(item) && getPartialMinPay(item) != null;
              const unset = isPartialLineAmountUnset(item);
              return (
                <Box key={item.id || idRaw} sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="body1" sx={{ pr: 1 }}>
                      {item.quantity} × {item.name}
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" component="div">
                        {unset ? '—' : (
                          <>
                            ${getLineChargeTotal(item).toFixed(2)}
                            {getLineChargeTotal(item) + 0.01 < lineList && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                of ${lineList.toFixed(2)} full
                              </Typography>
                            )}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  {showPartial && (
                    <PartialLinePayField
                      itemId={idRaw}
                      lineList={lineList}
                      minPay={getPartialMinPay(item)}
                      value={item.pay_line_total}
                      payLineCleared={!!item.pay_line_cleared}
                      onCommit={updateItemPayLineTotal}
                      onClear={clearItemPayLineTotal}
                    />
                  )}
                </Box>
              );
            })}
            {Array.isArray(cartContext) && cartContext.some((i) => isPartialPaymentEligible(i)) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {`Partial payment is available for lines of at least $${PARTIAL_MIN_LINE_DOLLARS.toFixed(0)} (price × quantity). The minimum you can pay on a partial is ${PARTIAL_MIN_PAY_FRACTION * 100}% of that line.`}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" color="text.secondary">
                Subtotal
              </Typography>
              <Typography variant="body1">${orderSummary.subtotal.toFixed(2)}</Typography>
            </Box>
            {orderSummary.tax > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  Tax
                </Typography>
                <Typography variant="body1">
                  ${orderSummary.tax.toFixed(2)}
                </Typography>
              </Box>
            )}
            {orderSummary.fee > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  {process.env.REACT_APP_CREDIT_CARD_FEE_NAME || 'Credit Card Processing Fee'}
                </Typography>
                <Typography variant="body1">
                  ${orderSummary.fee.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, mt: 2 }}>
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6">${orderSummary.total.toFixed(2)}</Typography>
            </Box>
          </Paper>

          {/* Payment Method Selector moved into left column to match width */}
          <Paper elevation={0} sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            background: 'linear-gradient(180deg, #f1f4fa 0%, #e8edf7 100%)',
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.08)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.08)'
          }}>
            <Typography variant="h6" gutterBottom>
              Select Payment Method
            </Typography>
            <Divider sx={{ my: 2 }} />
            {/* Clickable image options */}
            <Grid container spacing={2} sx={{ mb: 1, justifyContent: 'center', flexWrap: 'nowrap' }}>
              <Grid item xs={6} sm={6} md={6} sx={{ maxWidth: 320, flex: '1 1 0' }}>
                <Card elevation={0} sx={{
                  height: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: isCardPaymentSelected ? '3px solid' : '1px solid',
                  borderColor: isCardPaymentSelected ? 'primary.main' : 'rgba(0,0,0,0.12)',
                  boxShadow: isCardPaymentSelected
                    ? '0 0 0 4px rgba(25, 118, 210, 0.22), 0 14px 32px rgba(25, 118, 210, 0.18)'
                    : '0 8px 20px rgba(0,0,0,0.08)',
                  transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                  transform: isCardPaymentSelected ? 'translateY(-2px)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isCardPaymentSelected
                      ? '0 0 0 4px rgba(25, 118, 210, 0.28), 0 18px 38px rgba(25, 118, 210, 0.2)'
                      : '0 14px 28px rgba(0,0,0,0.12)'
                  },
                  '&:active': { transform: 'translateY(-1px)' }
                }}>
                  <CardActionArea onClick={() => {
                    if (hasIncompletePartial) {
                      setPaymentStatus('error');
                      setPaymentError('Please enter the initial payment amount for each line before choosing a payment method.');
                      return;
                    }
                    if (zelleRefCode) cancelZellePending(zelleRefCode);
                    setZelleDialogOpen(false);
                    setPaymentMethod('swipe');
                    setStatusHint('');
                    setPaymentError(null);
                    setPaymentStatus(null);
                  }}>
                    <CardMedia
                      component="img"
                      image={process.env.PUBLIC_URL + '/assets/images/payment_cc.png'}
                      alt="Card Payment"
                      sx={{ height: 140, objectFit: 'cover' }}
                    />
                    <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(25,118,210,0.14)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        Card Payment (Terminal)
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={6} sx={{ maxWidth: 320, flex: '1 1 0' }}>
                <Card elevation={0} sx={{
                  height: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: isZellePaymentSelected ? '3px solid' : '1px solid',
                  borderColor: isZellePaymentSelected ? '#7b1fa2' : 'rgba(123, 31, 162, 0.25)',
                  boxShadow: isZellePaymentSelected
                    ? '0 0 0 4px rgba(123, 31, 162, 0.22), 0 14px 32px rgba(123, 31, 162, 0.14)'
                    : '0 8px 20px rgba(0,0,0,0.08)',
                  transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                  transform: isZellePaymentSelected ? 'translateY(-2px)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isZellePaymentSelected
                      ? '0 0 0 4px rgba(123, 31, 162, 0.28), 0 16px 34px rgba(123, 31, 162, 0.18)'
                      : '0 14px 28px rgba(0,0,0,0.12)'
                  },
                  '&:active': { transform: 'translateY(-1px)' }
                }}>
                  <CardActionArea onClick={async () => {
                    if (hasIncompletePartial) {
                      setPaymentStatus('error');
                      setPaymentError('Please enter the initial payment amount for each line before choosing a payment method.');
                      return;
                    }
                    if (zelleRefCode) cancelZellePending(zelleRefCode);
                    setPaymentMethod('zelle');
                    setZelleDialogOpen(false);
                    setPaymentError(null);
                    setPaymentStatus(null);
                    if (currentPaymentIntentId) {
                      try {
                        await cancelPaymentIntent(currentPaymentIntentId);
                      } catch {}
                      try { clearActivePaymentIntent?.(); } catch {}
                      setCurrentPaymentIntentId(null);
                    } else {
                      try { clearActivePaymentIntent?.(); } catch {}
                    }
                    if (isGuestPaymentContext) return;
                    try {
                      await generateZelleCode();
                      setZelleDialogOpen(true);
                      setZelleConfirmed(false);
                    } catch (e) {
                      setPaymentStatus('error');
                      setPaymentError('Unable to process zelle payments. Please try another payment method.');
                    }
                  }}>
                    <CardMedia
                      component="img"
                      image={process.env.PUBLIC_URL + '/assets/images/payment_zelle.png'}
                      alt="Zelle Payment"
                      sx={{ height: 140, objectFit: 'cover' }}
                    />
                    <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(156,39,176,0.08)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#333333' }}>
                        Zelle Payment
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
            {/* Legacy text buttons removed; helper caption removed for a cleaner UI */}
          </Paper>

          {isGuestPaymentContext && paymentMethod && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                background: 'linear-gradient(180deg, #fffde7 0%, #fff9c4 100%)',
                border: '2px solid',
                borderColor: '#fbc02d',
                boxShadow: '0 6px 18px rgba(251, 192, 45, 0.25)'
              }}
            >
              <FormControlLabel
                sx={{
                  alignItems: 'flex-start',
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '1.35rem',
                    fontWeight: 700,
                    lineHeight: 1.35,
                    color: 'text.primary',
                    pt: 0.5
                  }
                }}
                control={
                  <Checkbox
                    checked={guestWantsReceipt}
                    onChange={(e) => {
                      setGuestWantsReceipt(e.target.checked);
                      if (!e.target.checked) {
                        setGuestReceiptFirstName('');
                        setGuestReceiptLastName('');
                        setGuestReceiptEmail('');
                      }
                    }}
                    sx={{
                      p: 0.75,
                      '& .MuiSvgIcon-root': { fontSize: 36 }
                    }}
                  />
                }
                label="Email me a receipt"
              />
              {guestWantsReceipt && (
                <Box sx={{ mt: 2, pl: 0.5 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="First name"
                        value={guestReceiptFirstName}
                        onChange={(e) => setGuestReceiptFirstName(e.target.value)}
                        onFocus={openGuestReceiptFirstNameKeyboard}
                        InputLabelProps={{ sx: { fontSize: '1.05rem' } }}
                        inputProps={{ sx: { fontSize: '1.15rem', py: 1.25 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Last name"
                        value={guestReceiptLastName}
                        onChange={(e) => setGuestReceiptLastName(e.target.value)}
                        onFocus={openGuestReceiptLastNameKeyboard}
                        InputLabelProps={{ sx: { fontSize: '1.05rem' } }}
                        inputProps={{ sx: { fontSize: '1.15rem', py: 1.25 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Email address"
                        type="email"
                        value={guestReceiptEmail}
                        onChange={(e) => setGuestReceiptEmail(e.target.value)}
                        onFocus={openGuestReceiptEmailKeyboard}
                        InputLabelProps={{ sx: { fontSize: '1.05rem' } }}
                        inputProps={{ sx: { fontSize: '1.15rem', py: 1.25 } }}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '1rem' }}>
                    Receipt only — this does not create a temple account.
                  </Typography>
                </Box>
              )}
              {paymentMethod === 'zelle' && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleStartZellePayment}
                  disabled={hasIncompletePartial}
                  sx={{ mt: 2.5, py: 1.5, fontSize: '1.1rem' }}
                >
                  {`Continue to Pay — $${orderSummary.total.toFixed(2)}`}
                </Button>
              )}
            </Paper>
          )}

          {/* Inline hint below the selection box (shows after 15s). No close button. */}
          {selectMethodHintOpen && !paymentMethod && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" variant="filled">Please select a payment method to continue.</Alert>
            </Box>
          )}
          {/* Card Reader Payment */}
          {paymentMethod === 'swipe' && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon /> Card Payment
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{
              mt: 3,
              p: 3,
              bgcolor: 'action.hover',
              borderRadius: 1,
              textAlign: 'center',
              mb: 2
            }}>
              {/* Processing Fees Box */}
              <Box sx={{ 
                p: 2.5, 
                mb: 3, 
                bgcolor: '#fff8e1', 
                borderRadius: 2,
                border: '1px solid #ffe0b2',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                textAlign: 'left'
              }}>
                <Typography variant="subtitle2" sx={{ 
                  color: '#e65100', 
                  fontWeight: 'bold', 
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  Processing fees:
                </Typography>
                
                <Typography variant="body2" sx={{ 
                  color: '#e65100', 
                  mb: 1.5,
                  lineHeight: 1.6
                }}>
                  Please consider covering the {feePercentDisplay}% processing fee (min. ${flatFeeAmount.toFixed(2)} for payments under ${(10).toFixed(2)}) so your full donation supports the temple.
                </Typography>
                
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: 'rgba(156, 39, 176, 0.07)',
                  p: 1.5,
                  borderRadius: 1,
                  borderLeft: '3px solid #9c27b0'
                }}>
                  <span style={{ color: '#9c27b0', marginTop: '2px' }}>💡</span>
                  <Typography variant="body2" sx={{ color: '#4a148c' }}>
                    <strong>Tip:</strong> Use Zelle to avoid fees and ensure 100% of your donation goes to the temple.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePaymentSubmit}
                  disabled={isProcessing || hasIncompletePartial}
                  startIcon={isProcessing ? <CircularProgress size={24} /> : <CreditCardIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled'
                    }
                  }}
                >
                  {isProcessing ? 'Processing payment...' : `Continue to Pay — $${orderSummary.total.toFixed(2)}`}
                </Button>
                {statusHint && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {statusHint}
                  </Typography>
                )}
                {paymentStatus === 'error' && paymentError && (
                  <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Alert id="payment-error-message" severity="error" sx={{ mb: 1 }}>
                      {paymentError}
                    </Alert>
                    {currentPaymentIntentId && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleCancelCurrentPaymentIntent}
                        disabled={isProcessing}
                      >
                        Cancel Payment
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  <LockIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Your payment is secure and encrypted.
                </Typography>
              </Box>
            </Box>
          </Paper>
          )}

          {/* Zelle inline box removed; we now use a blocking dialog */}

          {/* Zelle guidance access removed */}
        </Box>
      </Box>

{/* Zelle Payment Dialog (blocking) */}
  <ZelleDialog
    open={Boolean(zelleDialogOpen && paymentMethod === 'zelle')}
    onClose={() => { /* blocked to keep focus */ }}
    amount={zelleAmount}
    refCode={zelleRefCode}
    waiting={zelleWaiting}
    confirmed={zelleConfirmed}
    onCancel={() => {
      if (zelleRefCode) cancelZellePending(zelleRefCode);
      setZelleDialogOpen(false);
      setPaymentMethod(null);
    }}
    warning={zelleShowWarning}
    remainingMs={zelleRemainingMs}
    onExtend={handleZelleExtend}
    isGrace={zelleGraceActive}
    canExtend={!zelleExtensionUsed}
    errorMessage={zelleFailed ? zelleErrorMessage : ''}
    onTryDifferentMethod={() => {
      if (zelleRefCode) cancelZellePending(zelleRefCode);
      setZelleFailed(false);
      setZelleErrorMessage('');
      setZelleDialogOpen(false);
      setPaymentMethod(null);
    }}
    onRetryZelle={() => {
      setZelleFailed(false);
      setZelleErrorMessage('');
      setZelleWaiting(true);
    }}
  />

{/* Terminal Card Processing Dialog */}
<Dialog
  open={Boolean(isProcessing && paymentMethod === 'swipe')}
  onClose={() => { /* blocked to keep focus */ }}
  PaperProps={{
    sx: {
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'rgba(0,0,0,0.08)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
    }
  }}
>
  <DialogTitle sx={{
    fontWeight: 800,
    pb: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    pr: 5
  }}>
    <TapAndPlayIcon color="primary" />
    Ready – Tap, Insert, or Swipe
    <IconButton
      aria-label="close"
      onClick={abortProcessingAndReset}
      sx={{ position: 'absolute', right: 8, top: 8 }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent dividers sx={{
    background: 'linear-gradient(180deg, #f6f9ff 0%, #eef3fd 100%)',
    textAlign: 'center',
    py: 4
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, justifyContent: 'center' }}>
      <CircularProgress size={30} thickness={4} />
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {statusHint || 'Please tap your card on the terminal below'}
      </Typography>
    </Box>

    {/* Terminal and Hand images side by side */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      gap: 3,
      mb: 3,
      flexWrap: 'wrap'
    }}>
      {/* Stripe WisePOS device image - 60% size */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        '& img': {
          maxWidth: '168px', // 60% of 280px
          width: '100%',
          height: 'auto',
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
        }
      }}>
        <img 
          src="/assets/images/stripe_wisepos.png" 
          alt="Stripe Terminal - Tap your card here" 
        />
      </Box>

      {/* Animated hand image pointing to terminal */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        animation: `${handBounceAnimation} 2s ease-in-out infinite`,
        '& img': {
          maxWidth: '140px', // Proportional to terminal for good visual appeal
          width: '100%',
          height: 'auto',
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))'
        }
      }}>
        <img 
          src="/assets/images/hand.png" 
          alt="Pointing to terminal" 
        />
      </Box>
    </Box>

    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
      Secure and encrypted processing. Do not remove your card until instructed.
    </Typography>
  </DialogContent>
</Dialog>

</Box>
</PageLayout>
);
};

export default PaymentPage;
